import { useEffect, useRef, useState } from "react";
import { ArrowRight, MapPin, Search } from "lucide-react";
import type { Store } from "@little-finds/core";
import { api, demo } from "../api";

export function DirectoryBrowser({
  onSelect,
}: {
  onSelect: (storeId: string) => Promise<void>;
}) {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [stores, setStores] = useState<Store[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const request = useRef(0);

  useEffect(() => {
    const current = ++request.current;
    setLoading(true);
    setLoadingMore(false);
    setStores([]);
    setHasMore(false);
    setError("");
    api
      .storePage(query)
      .then((page) => {
        if (current !== request.current) return;
        setStores(page.stores);
        setHasMore(page.has_more);
      })
      .catch((e: Error) => {
        if (current === request.current) setError(e.message);
      })
      .finally(() => {
        if (current === request.current) setLoading(false);
      });
    return () => {
      request.current += 1;
    };
  }, [query, retry]);

  async function more() {
    const current = request.current;
    setLoadingMore(true);
    setError("");
    try {
      const page = await api.storePage(query, stores.length);
      if (current !== request.current) return;
      setStores((previous) => [
        ...previous,
        ...page.stores.filter((s) => !previous.some((p) => p.id === s.id)),
      ]);
      setHasMore(page.has_more);
    } catch (e) {
      if (current === request.current) setError((e as Error).message);
    } finally {
      if (current === request.current) setLoadingMore(false);
    }
  }

  return (
    <div className="directory-browser stack">
      <p className="directory-notice">
        <strong>Shop directory · stock not checked</strong>
        {demo
          ? " These are fictional shops for trying the app."
          : " These are known retail branches, not confirmed product sightings. Call ahead before making a trip."}
      </p>
      <form
        className="directory-search"
        onSubmit={(e) => {
          e.preventDefault();
          const next = input.trim();
          if (next === query) setRetry((n) => n + 1);
          else setQuery(next);
        }}
      >
        <label htmlFor="directory-query">Shop name, city, or ZIP</label>
        <div>
          <input
            id="directory-query"
            value={input}
            maxLength={120}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Try a city or shop name"
          />
          <button className="button secondary" type="submit">
            <Search size={16} />
            Search
          </button>
        </div>
      </form>
      {error && (
        <div className="form-error" role="alert">
          <p>{error}</p>
          <button
            className="text-button"
            onClick={() =>
              stores.length ? void more() : setRetry((n) => n + 1)
            }
          >
            Try again
          </button>
        </div>
      )}
      {loading ? (
        <p role="status">Looking for known shops…</p>
      ) : (
        <>
          <p className="small muted" role="status">
            {stores.length
              ? `${stores.length}${hasMore ? "+" : ""} directory ${stores.length === 1 && !hasMore ? "shop" : "shops"}`
              : error
                ? "Shop directory is temporarily unavailable."
                : query
                  ? "No listed shops match that search. Try another city or shop name."
                  : "No shop locations are listed yet."}
          </p>
          <div className="directory-list" aria-label="Known shops">
            {stores.map((store) => (
              <button
                key={store.id}
                className="directory-shop"
                disabled={opening !== null}
                onClick={async () => {
                  setOpening(store.id);
                  try {
                    await onSelect(store.id);
                  } finally {
                    setOpening(null);
                  }
                }}
              >
                <MapPin size={19} aria-hidden="true" />
                <span>
                  <strong>{store.name}</strong>
                  <span>{store.address}</span>
                  <span>
                    {store.city}, {store.state} {store.zip}
                  </span>
                  <small>
                    {opening === store.id
                      ? "Opening shop…"
                      : "Stock not checked"}
                  </small>
                </span>
                <ArrowRight size={17} aria-hidden="true" />
              </button>
            ))}
          </div>
          {hasMore && (
            <button
              className="button secondary"
              disabled={loadingMore || opening !== null}
              onClick={() => void more()}
            >
              {loadingMore ? "Loading shops…" : "Load more shops"}
            </button>
          )}
        </>
      )}
    </div>
  );
}
